/**
 * Purchase Returns — HQ page.
 * Lifecycle: draft -> approved -> posted -> (cancelled).
 * Create dialog: vendor -> completed GRN (last 24 months) -> tick line items with per-line qty
 * -> single PR-level reason (Damaged/Expired/Wrong Item/Other) + notes -> save draft.
 * Row actions: Approve (draft), Post (approved), Cancel (any non-cancelled), View, PDF.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Plus, Search, Filter, Loader2, Undo2, FileText, CheckCircle, Send, XCircle, Download,
  ChevronsUpDown, Check, Package, Eye, ScanLine
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Popover, PopoverTrigger, PopoverContent
} from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList
} from '@/components/ui/command';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { purchaseReturnsAPI, vendorsAPI, grnV2API, productsAPI } from '@/services/api';
import { formatDate } from '@/utils/dateFormat';

const REASONS = [
  { value: 'damaged', label: 'Damaged' },
  { value: 'expired', label: 'Expired' },
  { value: 'wrong_item', label: 'Wrong Item' },
  { value: 'other', label: 'Other' }
];

const STATUS_STYLES = {
  draft: 'bg-gray-100 text-gray-700',
  approved: 'bg-blue-100 text-blue-700',
  posted: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-600'
};

const emptyForm = () => ({
  return_date: new Date().toISOString().slice(0, 10),
  vendor_id: '',
  grn_id: '',
  grn_type: 'local',
  reason: 'damaged',
  notes: '',
  items: []  // { grn_item_id, qty, rate, product_name, sku, lot_batch_number, accepted_qty }
});

export default function PurchaseReturnsPage() {
  const { toast } = useToast();
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [vendors, setVendors] = useState([]);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm());

  // GRN picker
  const [grnOptions, setGrnOptions] = useState([]);
  const [grnPickerLoading, setGrnPickerLoading] = useState(false);
  const [grnPickerOpen, setGrnPickerOpen] = useState(false);
  const [selectedGrn, setSelectedGrn] = useState(null);
  const [codeMap, setCodeMap] = useState({});       // product_id -> { barcode, barcodes[] }
  const [lineSearch, setLineSearch] = useState('');
  const [scanHit, setScanHit] = useState(null);     // item_id highlighted after a scan

  // View / detail dialog
  const [viewNote, setViewNote] = useState(null);

  const fetchReturns = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status_filter = statusFilter;
      const res = await purchaseReturnsAPI.list(params);
      setReturns(res.data || []);
    } catch (e) {
      toast({ title: 'Failed to load', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, toast]);

  const fetchVendors = useCallback(async () => {
    try {
      const res = await vendorsAPI.list();
      setVendors(res.data || []);
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => { fetchReturns(); }, [fetchReturns]);
  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  // Load completed GRNs for the selected vendor + type (last 24 months)
  const fetchGrnOptions = useCallback(async (vendorId, grnType) => {
    if (!vendorId) { setGrnOptions([]); return; }
    try {
      setGrnPickerLoading(true);
      const twoYearsAgo = new Date();
      twoYearsAgo.setMonth(twoYearsAgo.getMonth() - 24);
      const params = {
        vendor_id: vendorId,
        status: 'completed',
        from_date: twoYearsAgo.toISOString().slice(0, 10)
      };
      const res = grnType === 'local'
        ? await grnV2API.listLocal(params)
        : await grnV2API.listInternational(params);
      setGrnOptions(res.data || []);
    } catch (e) {
      setGrnOptions([]);
    } finally {
      setGrnPickerLoading(false);
    }
  }, []);

  useEffect(() => {
    setSelectedGrn(null);
    setForm(f => ({ ...f, grn_id: '', items: [] }));
    if (form.vendor_id) fetchGrnOptions(form.vendor_id, form.grn_type);
    else setGrnOptions([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.vendor_id, form.grn_type]);

  const handlePickGrn = async (summary) => {
    setGrnPickerOpen(false);
    try {
      setGrnPickerLoading(true);
      const res = form.grn_type === 'local'
        ? await grnV2API.getLocal(summary.id)
        : await grnV2API.getInternational(summary.id);
      const full = res.data;
      setSelectedGrn(full);
      setLineSearch(''); setScanHit(null); setCodeMap({});
      setForm(f => ({
        ...f,
        grn_id: full.id,
        items: []
      }));
      const pids = [...new Set((full.items || []).map(i => i.product_id).filter(Boolean))];
      if (pids.length) productsAPI.barcodesLookup(pids).then(r => setCodeMap(r.data || {})).catch(() => {});
    } catch (e) {
      toast({ title: 'Could not load GRN', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally {
      setGrnPickerLoading(false);
    }
  };

  const toggleItem = (grnItem) => {
    setForm(f => {
      const exists = f.items.find(i => i.grn_item_id === grnItem.item_id);
      if (exists) {
        return { ...f, items: f.items.filter(i => i.grn_item_id !== grnItem.item_id) };
      }
      const maxQty = Number(grnItem.accepted_qty || grnItem.received_qty || 0);
      const rate = Number(grnItem.unit_price || grnItem.basic_cost || 0);
      // Default discount = what the GRN line actually gave per unit: (subtotal − taxable) / qty
      const grnSubtotal = Number(grnItem.subtotal || 0);
      const discAmt = grnSubtotal > 0 && grnItem.taxable_amount !== undefined && maxQty > 0
        ? Math.max(0, (grnSubtotal - Number(grnItem.taxable_amount)) / maxQty)
        : Number(grnItem.discount_amount || 0) + Number(grnItem.scheme_discount_amount || 0);
      const discPct = rate ? +(discAmt * 100 / rate).toFixed(2) : 0;
      return {
        ...f,
        items: [...f.items, {
          grn_item_id: grnItem.item_id,
          product_name: grnItem.product_name,
          sku: grnItem.sku,
          lot_batch_number: grnItem.lot_batch_number,
          accepted_qty: maxQty,
          qty: maxQty,
          rate,
          gst_percent: Number(grnItem.gst_percent || 0),
          discount_percent: discPct,
          discount_amount: +discAmt.toFixed(2),
        }]
      };
    });
  };

  // --- search / scan inside the GRN lines -------------------------------------------------
  const codesOf = (it) => {
    const extra = codeMap[it.product_id]?.barcodes || [];
    return [...new Set([it.barcode, codeMap[it.product_id]?.barcode, ...extra].filter(Boolean).map(String))];
  };
  const norm = (c) => String(c || '').trim().toLowerCase().replace(/^0+(?=\d)/, '');
  const lineMatches = (it, s) => {
    if (!s) return true;
    const q = s.toLowerCase();
    return (it.product_name || '').toLowerCase().includes(q) || (it.sku || '').toLowerCase().includes(q)
      || (it.lot_batch_number || '').toLowerCase().includes(q) || codesOf(it).some(c => c.toLowerCase().includes(q));
  };
  const visibleGrnItems = (selectedGrn?.items || []).filter(it => lineMatches(it, lineSearch.trim()));

  // Enter (barcode scanner suffix) → exact match on any barcode / SKU → tick the line (or +1 qty if already ticked)
  const handleScan = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const code = lineSearch.trim();
    if (!code) return;
    const items = selectedGrn?.items || [];
    const exact = items.filter(it => codesOf(it).some(c => norm(c) === norm(code)) || norm(it.sku) === norm(code));
    const hits = exact.length ? exact : items.filter(it => lineMatches(it, code));
    if (hits.length === 0) return toast({ title: 'Not in this GRN', description: `No line matches "${code}"`, variant: 'destructive' });
    if (hits.length > 1 && !exact.length) return; // typed text narrowing the list — leave as filter
    const it = hits[0];
    const picked = form.items.find(i => i.grn_item_id === it.item_id);
    if (!picked) { toggleItem(it); setForm(f => ({ ...f, items: f.items.map(i => i.grn_item_id === it.item_id ? { ...i, qty: 1 } : i) })); }
    else {
      const max = Number(it.accepted_qty ?? it.received_qty ?? 0);
      const next = Math.min(max || Infinity, Number(picked.qty || 0) + 1);
      updateItem(it.item_id, 'qty', String(next));
      if (max && Number(picked.qty) >= max) toast({ title: 'Max reached', description: `Only ${max} received on this line` });
    }
    setScanHit(it.item_id); setLineSearch('');
    setTimeout(() => document.getElementById(`pr-line-${it.item_id}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 50);
    if (hits.length > 1) toast({ title: `${hits.length} batches match this barcode`, description: `Selected batch ${it.lot_batch_number || '-'} — tick another batch manually if needed` });
  };

  // Disc % and Disc Amt (per unit) stay in step, exactly like the GRN screen
  const updateItem = (grnItemId, field, value) => {
    setForm(f => ({
      ...f,
      items: f.items.map(i => {
        if (i.grn_item_id !== grnItemId) return i;
        const next = { ...i, [field]: value };
        const rate = parseFloat(field === 'rate' ? value : i.rate) || 0;
        if (field === 'discount_percent') next.discount_amount = +((rate * (parseFloat(value) || 0)) / 100).toFixed(2);
        else if (field === 'discount_amount') next.discount_percent = rate ? +(((parseFloat(value) || 0) * 100) / rate).toFixed(2) : 0;
        else if (field === 'rate') next.discount_amount = +((rate * (parseFloat(i.discount_percent) || 0)) / 100).toFixed(2);
        return next;
      })
    }));
  };

  const lineCalc = (i) => {
    const qty = parseFloat(i.qty) || 0;
    const rate = parseFloat(i.rate) || 0;
    const discUnit = Math.min(rate, Math.max(0, parseFloat(i.discount_amount) || 0));
    const gross = qty * rate;
    const discount = qty * discUnit;
    const taxable = gross - discount;
    const tax = taxable * (parseFloat(i.gst_percent) || 0) / 100;
    return { gross, discount, taxable, tax, total: taxable + tax };
  };
  const preview = form.items.reduce((acc, i) => {
    const c = lineCalc(i);
    return { gross: acc.gross + c.gross, discount: acc.discount + c.discount, taxable: acc.taxable + c.taxable, tax: acc.tax + c.tax, total: acc.total + c.total };
  }, { gross: 0, discount: 0, taxable: 0, tax: 0, total: 0 });
  const totalPreview = preview.total;

  const resetForm = () => {
    setForm(emptyForm());
    setSelectedGrn(null);
    setGrnOptions([]);
  };

  const handleCreate = async () => {
    if (!form.vendor_id) return toast({ title: 'Pick a vendor', variant: 'destructive' });
    if (!form.grn_id) return toast({ title: 'Pick a source GRN', variant: 'destructive' });
    if (form.items.length === 0) return toast({ title: 'Tick at least one item to return', variant: 'destructive' });
    for (const i of form.items) {
      if (!(parseFloat(i.qty) > 0)) return toast({ title: `Qty missing for ${i.product_name}`, variant: 'destructive' });
      if (parseFloat(i.qty) > i.accepted_qty) return toast({
        title: `Qty too high for ${i.product_name}`,
        description: `Max ${i.accepted_qty} (received)`,
        variant: 'destructive'
      });
    }
    try {
      setCreating(true);
      const payload = {
        return_date: form.return_date,
        vendor_id: form.vendor_id,
        grn_id: form.grn_id,
        grn_type: form.grn_type,
        reason: form.reason,
        notes: form.notes || null,
        items: form.items.map(i => ({
          grn_item_id: i.grn_item_id,
          qty: parseFloat(i.qty),
          rate: parseFloat(i.rate),
          discount_percent: parseFloat(i.discount_percent) || 0,
          discount_amount: parseFloat(i.discount_amount) || 0
        }))
      };
      const res = await purchaseReturnsAPI.create(payload);
      toast({ title: 'Draft saved', description: `${res.data.return_number} saved. Approve and post to update stock + ledger.` });
      setCreateOpen(false);
      resetForm();
      fetchReturns();
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally {
      setCreating(false);
    }
  };

  const runAction = async (fn, note, okTitle) => {
    try {
      await fn(note.id);
      toast({ title: okTitle, description: note.return_number });
      fetchReturns();
    } catch (e) {
      toast({ title: 'Action failed', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    }
  };

  const handleCancel = async (note) => {
    const reason = window.prompt(`Cancel ${note.return_number}? Enter a reason:`);
    if (!reason) return;
    try {
      await purchaseReturnsAPI.cancel(note.id, reason);
      toast({ title: 'Cancelled', description: note.return_number });
      fetchReturns();
    } catch (e) {
      toast({ title: 'Cancel failed', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    }
  };

  const filtered = returns.filter(r => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      (r.return_number || '').toLowerCase().includes(s) ||
      (r.vendor_name || '').toLowerCase().includes(s) ||
      (r.grn_number || '').toLowerCase().includes(s)
    );
  });

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Undo2 className="w-7 h-7 text-orange-600" /> Purchase Returns
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Return goods to vendors against a completed GRN. Stock and vendor ledger update automatically on post.
          </p>
        </div>
        <Button onClick={() => { resetForm(); setCreateOpen(true); }} data-testid="create-pr-btn">
          <Plus className="w-4 h-4 mr-1" /> New Purchase Return
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search PR#, vendor, GRN…"
                className="pl-9"
                data-testid="pr-search"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="pr-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="posted">Posted</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 flex justify-center text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-gray-500">
              <Undo2 className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="font-medium">No purchase returns yet</p>
              <p className="text-sm">Create one to send goods back to a vendor.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>PR #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>GRN Ref</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(r => (
                  <TableRow key={r.id} data-testid={`pr-row-${r.return_number}`}>
                    <TableCell className="font-mono text-sm">{r.return_number}</TableCell>
                    <TableCell>{formatDate(r.return_date)}</TableCell>
                    <TableCell>{r.vendor_name}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="text-sm">{r.grn_number}</span>
                        <span className="text-[10px] text-gray-400">{r.grn_type}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {REASONS.find(x => x.value === r.reason)?.label || r.reason}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      ₹{Math.round(Number(r.grand_total || 0)).toLocaleString('en-IN')}
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_STYLES[r.status] || 'bg-gray-100'}>{r.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setViewNote(r)} title="View">
                          <Eye className="w-4 h-4" />
                        </Button>
                        {r.status === 'draft' && (
                          <Button size="sm" variant="outline" className="h-8"
                            onClick={() => runAction(purchaseReturnsAPI.approve, r, 'Approved')}
                            data-testid={`approve-${r.return_number}`}>
                            <CheckCircle className="w-3.5 h-3.5 mr-1 text-blue-600" /> Approve
                          </Button>
                        )}
                        {r.status === 'approved' && (
                          <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => runAction(purchaseReturnsAPI.post, r, 'Posted')}
                            data-testid={`post-${r.return_number}`}>
                            <Send className="w-3.5 h-3.5 mr-1" /> Post
                          </Button>
                        )}
                        {r.status !== 'cancelled' && (
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500 hover:text-red-600"
                            onClick={() => handleCancel(r)} title="Cancel"
                            data-testid={`cancel-${r.return_number}`}>
                            <XCircle className="w-4 h-4" />
                          </Button>
                        )}
                        <a href={purchaseReturnsAPI.pdfUrl(r.id)} target="_blank" rel="noreferrer" title="PDF">
                          <Button size="icon" variant="ghost" className="h-8 w-8">
                            <Download className="w-4 h-4" />
                          </Button>
                        </a>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={(v) => { setCreateOpen(v); if (!v) resetForm(); }}>
        <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Purchase Return</DialogTitle>
            <DialogDescription>
              Pick a vendor and one of their completed GRNs (last 24 months). Tick which items to send back and choose a single reason.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Return Date *</Label>
                <Input
                  type="date"
                  value={form.return_date}
                  onChange={(e) => setForm(f => ({ ...f, return_date: e.target.value }))}
                  data-testid="pr-date"
                />
              </div>
              <div>
                <Label>Vendor *</Label>
                <Select
                  value={form.vendor_id}
                  onValueChange={(v) => setForm(f => ({ ...f, vendor_id: v, grn_id: '', items: [] }))}
                >
                  <SelectTrigger data-testid="pr-vendor">
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {vendors.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>GRN Type *</Label>
                <Select value={form.grn_type} onValueChange={(v) => setForm(f => ({ ...f, grn_type: v, grn_id: '', items: [] }))}>
                  <SelectTrigger data-testid="pr-grn-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="local">Local GRN</SelectItem>
                    <SelectItem value="international">International GRN</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Source GRN * <span className="text-xs text-gray-500">(last 24 months, completed)</span></Label>
              <Popover open={grnPickerOpen} onOpenChange={setGrnPickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    disabled={!form.vendor_id}
                    className="w-full justify-between font-normal"
                    data-testid="pr-grn-picker"
                  >
                    {selectedGrn ? (
                      <span className="flex items-center gap-2 truncate">
                        <FileText className="w-4 h-4 text-blue-600" />
                        <span>{selectedGrn.grn_number}</span>
                        <span className="text-xs text-gray-500">· {formatDate(selectedGrn.received_date)}</span>
                      </span>
                    ) : (
                      <span className="text-gray-400">
                        {form.vendor_id ? 'Search & pick a GRN…' : 'Pick vendor first'}
                      </span>
                    )}
                    <ChevronsUpDown className="w-4 h-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[520px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search by GRN #, invoice, PO…" />
                    <CommandList>
                      {grnPickerLoading ? (
                        <div className="p-4 text-sm text-gray-500 flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                        </div>
                      ) : (
                        <>
                          <CommandEmpty>No completed GRNs in the last 24 months.</CommandEmpty>
                          <CommandGroup heading={`${grnOptions.length} completed GRN${grnOptions.length === 1 ? '' : 's'}`}>
                            {grnOptions.map(g => (
                              <CommandItem
                                key={g.id}
                                value={`${g.grn_number} ${g.invoice_number || ''} ${g.po_number || ''}`}
                                onSelect={() => handlePickGrn(g)}
                                data-testid={`pr-grn-opt-${g.grn_number}`}
                              >
                                <Check className={`w-4 h-4 mr-2 ${selectedGrn?.id === g.id ? 'opacity-100 text-emerald-600' : 'opacity-0'}`} />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">{g.grn_number}</span>
                                    <Badge variant="outline" className="text-[10px]">{formatDate(g.received_date)}</Badge>
                                  </div>
                                  <div className="text-[11px] text-gray-500 mt-0.5 truncate">
                                    {g.invoice_number ? `Inv: ${g.invoice_number}` : ''}
                                    {g.po_number ? ` · PO: ${g.po_number}` : ''}
                                    {g.grand_total ? ` · ₹${Math.round(Number(g.grand_total)).toLocaleString('en-IN')}` : ''}
                                  </div>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {selectedGrn && (
              <div className="border rounded-lg bg-orange-50/40 border-orange-200 p-3 space-y-2">
                <div className="flex items-center gap-2 text-sm text-orange-800">
                  <Package className="w-4 h-4" />
                  <span className="font-semibold">Line items from {selectedGrn.grn_number}</span>
                  <span className="text-xs text-orange-600">· {(selectedGrn.items || []).length} lines · {form.items.length} selected</span>
                </div>
                <div className="relative">
                  <ScanLine className="w-4 h-4 absolute left-2.5 top-2.5 text-orange-500" />
                  <Input
                    value={lineSearch}
                    onChange={(e) => { setLineSearch(e.target.value); setScanHit(null); }}
                    onKeyDown={handleScan}
                    placeholder="Scan barcode or type product name / SKU / batch — Enter selects the line (+1 qty each scan)"
                    className="pl-8 bg-white"
                    autoComplete="off"
                    data-testid="pr-line-search"
                  />
                  {lineSearch && <span className="absolute right-2 top-2 text-[11px] text-gray-500">{visibleGrnItems.length} of {(selectedGrn.items || []).length}</span>}
                </div>
                <div className="max-h-64 overflow-y-auto border rounded bg-white">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr className="text-left text-xs text-gray-600">
                        <th className="px-2 py-1.5 w-8"></th>
                        <th className="px-2 py-1.5 w-10">S.No</th>
                        <th className="px-2 py-1.5">Product</th>
                        <th className="px-2 py-1.5 text-right w-16">Recd</th>
                        <th className="px-2 py-1.5 text-right w-20">Return Qty</th>
                        <th className="px-2 py-1.5 text-right w-24">Rate</th>
                        <th className="px-2 py-1.5 text-right w-20">Disc %</th>
                        <th className="px-2 py-1.5 text-right w-24">Disc Amt</th>
                        <th className="px-2 py-1.5 text-right w-24">Taxable</th>
                        <th className="px-2 py-1.5 text-right w-24">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleGrnItems.length === 0 && (
                        <tr><td colSpan={10} className="px-2 py-6 text-center text-xs text-gray-400" data-testid="pr-line-empty">No line matches "{lineSearch}"</td></tr>
                      )}
                      {visibleGrnItems.map((it) => {
                        const idx = (selectedGrn.items || []).indexOf(it);
                        const picked = form.items.find(i => i.grn_item_id === it.item_id);
                        const calc = picked ? lineCalc(picked) : null;
                        const codes = codesOf(it);
                        return (
                          <tr key={it.item_id} id={`pr-line-${it.item_id}`} className={`border-t transition-colors ${scanHit === it.item_id ? 'bg-yellow-100 ring-1 ring-inset ring-yellow-400' : picked ? 'bg-emerald-50/40' : ''}`} data-testid={`pr-line-${it.sku}`}>
                            <td className="px-2 py-1.5">
                              <Checkbox
                                checked={!!picked}
                                onCheckedChange={() => toggleItem(it)}
                                data-testid={`pr-item-check-${it.sku}`}
                              />
                            </td>
                            <td className="px-2 py-1.5 text-xs text-gray-500">{idx + 1}</td>
                            <td className="px-2 py-1.5">
                              <div className="font-medium truncate max-w-[240px]">{it.product_name}</div>
                              <div className="text-[10px] text-gray-500 font-mono">
                                {it.sku}{it.lot_batch_number ? ` · Batch ${it.lot_batch_number}` : ''}
                              </div>
                              {codes.length > 0 && (
                                <div className="text-[10px] text-gray-400 font-mono" data-testid={`pr-item-barcodes-${it.sku}`} title={codes.join(', ')}>
                                  <ScanLine className="w-3 h-3 inline mr-0.5 -mt-0.5" />{codes[0]}{codes.length > 1 ? ` +${codes.length - 1} more` : ''}
                                </div>
                              )}
                            </td>
                            <td className="px-2 py-1.5 text-right text-gray-600">
                              {it.accepted_qty ?? it.received_qty ?? 0}
                            </td>
                            <td className="px-2 py-1.5">
                              <Input
                                type="number"
                                min={0}
                                max={it.accepted_qty ?? it.received_qty ?? undefined}
                                value={picked?.qty ?? ''}
                                onChange={(e) => updateItem(it.item_id, 'qty', e.target.value)}
                                disabled={!picked}
                                className="h-7 text-right"
                                data-testid={`pr-item-qty-${it.sku}`}
                              />
                            </td>
                            <td className="px-2 py-1.5">
                              <Input
                                type="number"
                                value={picked?.rate ?? ''}
                                onChange={(e) => updateItem(it.item_id, 'rate', e.target.value)}
                                disabled={!picked}
                                className="h-7 text-right"
                              />
                            </td>
                            <td className="px-2 py-1.5">
                              <Input
                                type="number" min={0} max={100} step="0.01"
                                value={picked?.discount_percent ?? ''}
                                onChange={(e) => updateItem(it.item_id, 'discount_percent', e.target.value)}
                                disabled={!picked}
                                className="h-7 text-right"
                                data-testid={`pr-item-disc-pct-${it.sku}`}
                              />
                            </td>
                            <td className="px-2 py-1.5">
                              <Input
                                type="number" min={0} step="0.01"
                                value={picked?.discount_amount ?? ''}
                                onChange={(e) => updateItem(it.item_id, 'discount_amount', e.target.value)}
                                disabled={!picked}
                                className="h-7 text-right"
                                title="Discount amount per unit"
                                data-testid={`pr-item-disc-amt-${it.sku}`}
                              />
                            </td>
                            <td className="px-2 py-1.5 text-right font-mono text-gray-600">
                              {calc ? `₹${calc.taxable.toFixed(2)}` : '—'}
                            </td>
                            <td className="px-2 py-1.5 text-right font-mono">
                              {calc ? `₹${calc.total.toFixed(2)}` : '—'}
                              {calc && calc.tax > 0 && <div className="text-[10px] text-gray-400">incl. tax ₹{calc.tax.toFixed(2)}</div>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Reason *</Label>
                <Select value={form.reason} onValueChange={(v) => setForm(f => ({ ...f, reason: v }))}>
                  <SelectTrigger data-testid="pr-reason">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REASONS.map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notes (Optional)</Label>
                <Input
                  value={form.notes}
                  onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Any additional details"
                  data-testid="pr-notes"
                />
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <div className="text-right text-sm space-y-0.5 min-w-[240px]" data-testid="pr-preview-totals">
                <div className="flex justify-between gap-6"><span className="text-gray-500">Subtotal</span><span>₹{preview.gross.toFixed(2)}</span></div>
                <div className="flex justify-between gap-6"><span className="text-gray-500">Discount</span><span className="text-red-600">− ₹{preview.discount.toFixed(2)}</span></div>
                <div className="flex justify-between gap-6"><span className="text-gray-500">Taxable</span><span>₹{preview.taxable.toFixed(2)}</span></div>
                <div className="flex justify-between gap-6"><span className="text-gray-500">Tax</span><span>₹{preview.tax.toFixed(2)}</span></div>
                <div className="flex justify-between gap-6 border-t pt-1"><span className="font-medium">Return Total</span>
                  <span className="text-2xl font-bold" data-testid="pr-total">₹{totalPreview.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateOpen(false); resetForm(); }} disabled={creating}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} data-testid="submit-pr-btn">
              {creating && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Save as Draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View / detail dialog */}
      <Dialog open={!!viewNote} onOpenChange={(v) => !v && setViewNote(null)}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
          {viewNote && (
            <>
              <DialogHeader>
                <DialogTitle>{viewNote.return_number}</DialogTitle>
                <DialogDescription>
                  {viewNote.vendor_name} · {viewNote.grn_number} · {formatDate(viewNote.return_date)}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Badge className={STATUS_STYLES[viewNote.status]}>{viewNote.status}</Badge>
                  <Badge variant="outline">Reason: {REASONS.find(r => r.value === viewNote.reason)?.label || viewNote.reason}</Badge>
                </div>
                {viewNote.notes && (
                  <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded">
                    <span className="font-medium">Notes:</span> {viewNote.notes}
                  </div>
                )}
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">S.No</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead>Batch</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                      <TableHead className="text-right">Disc %</TableHead>
                      <TableHead className="text-right">Disc Amt</TableHead>
                      <TableHead className="text-right">Taxable</TableHead>
                      <TableHead className="text-right">Tax</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(viewNote.items || []).map((it, idx) => (
                      <TableRow key={idx} data-testid={`pr-view-row-${idx + 1}`}>
                        <TableCell className="text-gray-500">{idx + 1}</TableCell>
                        <TableCell>
                          <div className="font-medium">{it.product_name}</div>
                          <div className="text-[10px] text-gray-500">{it.sku}</div>
                        </TableCell>
                        <TableCell className="font-mono text-xs">{it.lot_batch_number}</TableCell>
                        <TableCell className="text-right">{it.qty}</TableCell>
                        <TableCell className="text-right">₹{Number(it.rate).toFixed(2)}</TableCell>
                        <TableCell className="text-right">{Number(it.discount_percent || 0)}%</TableCell>
                        <TableCell className="text-right">₹{Number(it.gross_discount_amount || 0).toFixed(2)}</TableCell>
                        <TableCell className="text-right">₹{Number(it.taxable_amount ?? it.amount ?? 0).toFixed(2)}</TableCell>
                        <TableCell className="text-right text-gray-600">₹{Number(it.tax_amount || 0).toFixed(2)}</TableCell>
                        <TableCell className="text-right font-mono">₹{Number(it.total_amount).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="flex justify-end">
                  <div className="text-sm space-y-0.5 text-right min-w-[220px]" data-testid="pr-view-totals">
                    <div className="flex justify-between gap-6"><span className="text-gray-500">Subtotal</span><span>₹{Number(viewNote.subtotal || 0).toFixed(2)}</span></div>
                    {Number(viewNote.discount_amount) > 0 && <div className="flex justify-between gap-6"><span className="text-gray-500">Discount</span><span className="text-red-600">− ₹{Number(viewNote.discount_amount).toFixed(2)}</span></div>}
                    {viewNote.taxable_amount !== undefined && <div className="flex justify-between gap-6"><span className="text-gray-500">Taxable</span><span>₹{Number(viewNote.taxable_amount || 0).toFixed(2)}</span></div>}
                    <div className="flex justify-between gap-6"><span className="text-gray-500">Tax</span><span>₹{Number(viewNote.tax_amount || 0).toFixed(2)}</span></div>
                    <div className="flex justify-between gap-6 text-lg font-semibold border-t pt-1"><span>Total</span><span>₹{Number(viewNote.grand_total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></div>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Toaster />
    </div>
  );
}
